import {
  X,
  Calendar,
  ChevronDown,
  CheckCircle2,
  Timer,
} from "@tamagui/lucide-icons";
import {
  useServiceRequests,
  useCustomers,
  Customer,
  ServiceType,
  ServiceRequestStatus,
  useHubs,
  useSensors,
} from "common";
import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Card,
  Select,
  Label,
  ScrollView,
  Spinner,
  useMedia,
} from "tamagui";
import { GradientButton, OutlinedButton, Table } from "tamagui-config";

import {
  ServiceRequestDisplay,
  ServiceRequestTableRow,
} from "./ServiceRequestTableRow";
import { KPICard } from "../../components/KPICard";
import { serviceTypeLabel } from "../../utils";
import {
  formatDate,
  formatDateTime,
  formatTime,
  toDate,
} from "../../utils/formatDate";

const formatCustomerAddress = (customer: Customer | undefined): string => {
  if (!customer) return "Address not available";

  const addressParts: string[] = [];
  if (customer.address) addressParts.push(customer.address);
  if (customer.city) addressParts.push(customer.city);
  if (customer.state) addressParts.push(customer.state);
  if (customer.zipCode) addressParts.push(customer.zipCode);

  return addressParts.length > 0
    ? addressParts.join(", ")
    : "Address not available";
};

const ServiceRequestsList: React.FC = () => {
  const {
    serviceRequests,
    statusCounts,
    isLoading,
    error,
    fetchServiceRequests,
  } = useServiceRequests();
  const { customers, getCustomerById } = useCustomers();
  const { getHubById } = useHubs({ fetchAndSubscribeOnLoad: false });
  const { getSensorById } = useSensors();
  const { t } = useTranslation();
  const { lg, sm } = useMedia();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [pageCustomers, setPageCustomers] = useState<Map<string, any>>(
    new Map(),
  );
  const [pageHubs, setPageHubs] = useState<Map<string, any>>(new Map());
  const [pageSensors, setPageSensors] = useState<Map<string, any>>(new Map());

  // ?age=14d → only OPEN requests (not closed/canceled) created more than 14
  // days ago. No server-side date filter, so one big page + client filter.
  // ponytail: fine while requests < 1000; add a resolver arg past that.
  const ageDays = Number(/^(\d+)d$/.exec(searchParams.get("age") ?? "")?.[1]);
  const [filters, setFilters] = useState(() => {
    const customerParam = searchParams.get("customer") || "";
    const typeParam = searchParams.get("type") || "";
    const statusParam = searchParams.get("status") || "";
    const status = (Object.values(ServiceRequestStatus) as string[]).includes(
      statusParam,
    )
      ? statusParam
      : "All";

    return {
      status,
      customerId: customerParam,
      serviceType: typeParam,
    };
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    hasMore: false,
    tokens: [undefined] as (string | undefined)[],
  });

  const customerMap = useMemo(() => {
    const map = new Map<string, Customer>();
    customers.forEach((customer) => map.set(customer.id, customer));
    pageCustomers.forEach((customer, id) => map.set(id, customer));
    return map;
  }, [customers, pageCustomers]);

  const hubMap = useMemo(() => pageHubs, [pageHubs]);

  const sensorMap = useMemo(() => pageSensors, [pageSensors]);

  const serviceRequestsWithCustomer: ServiceRequestDisplay[] = useMemo(() => {
    const ageCutoff = ageDays ? Date.now() - ageDays * 86400 * 1000 : null;
    const requests = (Array.isArray(serviceRequests) ? serviceRequests : []).filter(
      (req) => {
        if (ageCutoff === null) return true;
        if (
          req.status === ServiceRequestStatus.CLOSED ||
          req.status === ServiceRequestStatus.CANCELED
        ) {
          return false;
        }
        const created = toDate(req.createdAt);
        return !!created && created.getTime() < ageCutoff;
      },
    );

    const mappedRequests = requests.map((req) => {
      const realReq = req as any;
      const customer = customerMap.get(req.customerId);
      const customerAddress = formatCustomerAddress(customer);
      const hub = hubMap.get(req.hubId || "");
      const sensor = sensorMap.get(req.sensorId || "");

      return {
        ...req,
        propertyId: req.hubId,
        createdAt: req.createdAt
          ? new Date(req.createdAt).toISOString()
          : new Date().toISOString(),
        updatedAt: req.updatedAt
          ? new Date(req.updatedAt).toISOString()
          : new Date().toISOString(),
        systemAlert: formatDateTime(realReq.alert?.timestamp),
        customerRequest: formatDateTime(req.requestedAt),
        requestClosed: formatDateTime(req.completedAt),
        requestedDate: formatDate(req.scheduledDate),
        requestedTimeSlot: req.scheduledDate
          ? formatTime(req.scheduledDate)
          : null,
        deviceRoom: (() => {
          // No hardcoded fallback name — a request whose hub row is gone
          // (deleted hub) must read as unknown, not masquerade as a real hub.
          const hubName = hub?.name || t("serviceRequests.unknownHub");
          const sensorLocation =
            sensor?.location ||
            realReq.deviceLocation ||
            realReq.roomType ||
            "Not specified";
          return `${hubName} / ${sensorLocation}`;
        })(),
        customerName: customer
          ? customer.name || customer.companyName || customer.email
          : "Unknown Customer",
        customerAddress,
        notes: realReq.notes || realReq.additionalNotes || "",
        hubName: hub?.name || t("serviceRequests.unknownHub"),
      };
    });

    return mappedRequests;
  }, [serviceRequests, customerMap, hubMap, sensorMap]);

  interface FilterOption {
    name: string;
    value: string;
  }

  const customerOptions = useMemo<FilterOption[]>(
    () => [
      { name: t("common.all"), value: "" },
      ...Array.from(customerMap.values())
        .filter((customer) => !!customer.name)
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
        .map((customer) => ({
          name: customer.name as string,
          value: customer.id,
        })),
    ],
    [customerMap],
  );

  const serviceTypeOptions: FilterOption[] = [
    { name: t("common.all"), value: "" },
    ...Object.entries(ServiceType).map(([key, value]) => ({
      name: serviceTypeLabel(t, key),
      value,
    })),
  ];

  const updateFilters = (newFilters: Partial<typeof filters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    setPagination({ currentPage: 1, hasMore: false, tokens: [undefined] });

    const newSearchParams = new URLSearchParams(searchParams);

    if ("customerId" in newFilters) {
      if (updatedFilters.customerId) {
        newSearchParams.set("customer", updatedFilters.customerId);
      } else {
        newSearchParams.delete("customer");
      }
    }

    // Update service type parameter
    if ("serviceType" in newFilters) {
      if (updatedFilters.serviceType) {
        newSearchParams.set("type", updatedFilters.serviceType);
      } else {
        newSearchParams.delete("type");
      }
    }

    setSearchParams(newSearchParams);
  };

  const fetchServiceRequestsForCurrentPage = async () => {
    const pageToken = pagination.tokens[pagination.currentPage - 1];
    const result = await fetchServiceRequests(ageDays ? 1000 : 10, pageToken, {
      customerId: filters.customerId || undefined,
      serviceType: filters.serviceType || undefined,
      status: filters.status === "All" ? undefined : filters.status,
    });
    if (result) {
      setPagination((prev) => ({
        ...prev,
        hasMore: result.hasMore,
        tokens:
          result.nextToken && !prev.tokens[prev.currentPage]
            ? [...prev.tokens.slice(0, prev.currentPage), result.nextToken]
            : prev.tokens,
      }));
    }
  };

  const goToNextPage = () =>
    pagination.hasMore &&
    setPagination((prev) => ({ ...prev, currentPage: prev.currentPage + 1 }));
  const goToPreviousPage = () =>
    pagination.currentPage > 1 &&
    setPagination((prev) => ({ ...prev, currentPage: prev.currentPage - 1 }));

  const handleViewServiceRequest = (id: string) => {
    navigate(`/service-requests/${id}`);
  };

  useEffect(() => {
    fetchServiceRequestsForCurrentPage();
  }, [pagination.currentPage, filters]);

  // Fetch hubs, sensors, and customers when service requests are loaded
  useEffect(() => {
    if (serviceRequests && serviceRequests.length > 0) {
      // Get unique customer IDs, hub IDs and sensor IDs from current page service requests
      const uniqueCustomerIds = [
        ...new Set(
          serviceRequests
            .filter((req) => req.customerId)
            .map((req) => req.customerId),
        ),
      ];

      const uniqueHubIds = [
        ...new Set(
          serviceRequests.filter((req) => req.hubId).map((req) => req.hubId!),
        ),
      ];

      const uniqueSensorIds = [
        ...new Set(
          serviceRequests
            .filter((req) => req.sensorId)
            .map((req) => req.sensorId!),
        ),
      ];

      // Fetch customers that are not already loaded
      uniqueCustomerIds.forEach(async (customerId) => {
        if (!customerMap.has(customerId) && !pageCustomers.has(customerId)) {
          try {
            const customer = await getCustomerById!(customerId);
            if (customer) {
              setPageCustomers((prev) =>
                new Map(prev).set(customerId, customer),
              );
            }
          } catch (error) {
            console.error(`Failed to fetch customer ${customerId}:`, error);
          }
        }
      });

      // Fetch only the hubs that are not already loaded
      uniqueHubIds.forEach(async (hubId) => {
        if (!pageHubs.has(hubId)) {
          try {
            const hub = await getHubById(hubId);
            if (hub) {
              setPageHubs((prev) => new Map(prev).set(hubId, hub));
            }
          } catch (error) {
            console.error(`Failed to fetch hub ${hubId}:`, error);
          }
        }
      });

      // Fetch individual sensors that are not already loaded
      uniqueSensorIds.forEach(async (sensorId) => {
        if (!pageSensors.has(sensorId)) {
          try {
            const sensor = await getSensorById(sensorId);
            if (sensor) {
              setPageSensors((prev) => new Map(prev).set(sensorId, sensor));
            }
          } catch (error) {
            console.error(`Failed to fetch sensor ${sensorId}:`, error);
          }
        }
      });
    }
  }, [serviceRequests, pageCustomers, pageHubs, pageSensors, customerMap]);

  // #region Render Functions
  const renderLoading = () => (
    <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
      <Spinner size="large" />
      <Text marginTop="$2">{t("common.loading", "Loading...")}</Text>
    </YStack>
  );

  const renderError = () => (
    <YStack padding="$4">
      <Text color="$red10" fontSize="$5">
        {t("common.error", "Error loading service requests.")}
      </Text>
    </YStack>
  );

  const renderHeader = () => (
    <XStack
      justifyContent="space-between"
      alignItems="center"
      marginBottom="$2"
    >
      <Text fontSize="$9" fontWeight="bold" color="$color">
        {t("serviceRequests.title", "Service Requests")}
      </Text>
      {/* <Button
        theme="active"
        size="$4"
        onPress={() => navigate("/service-requests/new")}
      >
        Create New Service Request
      </Button> */}
    </XStack>
  );

  const renderFilters = () => (
    <Card padding="$4" elevation="$1">
      <YStack space="$3">
        <XStack
          space="$3"
          alignItems="flex-end"
          $xs={{ flexDirection: "column", alignItems: "stretch" }}
        >
          <YStack space="$1" flex={1} minWidth={180}>
            <Label htmlFor="customer-filter" fontSize="$3">
              {t("serviceRequests.customers", "Customers")}
            </Label>
            <Select
              id="customer-filter"
              value={filters.customerId}
              onValueChange={(value) => updateFilters({ customerId: value })}
            >
              <Select.Trigger iconAfter={ChevronDown}>
                <Select.Value
                  placeholder={t("serviceRequests.selectCustomer", "All")}
                />
              </Select.Trigger>
              <Select.Content>
                <Select.Viewport>
                  {customerOptions.map((opt, i) => (
                    <Select.Item
                      index={i}
                      key={opt.value}
                      value={opt.value.toString()}
                    >
                      <Select.ItemText>{opt.name}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select>
          </YStack>

          <YStack space="$1" flex={1} minWidth={180}>
            <Label htmlFor="service-type-filter" fontSize="$3">
              {t("serviceRequests.serviceType", "Service Type")}
            </Label>
            <Select
              id="service-type-filter"
              value={filters.serviceType}
              onValueChange={(value) => updateFilters({ serviceType: value })}
            >
              <Select.Trigger iconAfter={ChevronDown}>
                <Select.Value
                  placeholder={t("serviceRequests.selectServiceType", "All")}
                />
              </Select.Trigger>
              <Select.Content>
                <Select.Viewport>
                  {serviceTypeOptions.map((opt, i) => (
                    <Select.Item
                      index={i}
                      key={opt.value}
                      value={opt.value.toString()}
                    >
                      <Select.ItemText>{opt.name}</Select.ItemText>
                    </Select.Item>
                  ))}
                </Select.Viewport>
              </Select.Content>
            </Select>
          </YStack>
        </XStack>
      </YStack>
    </Card>
  );

  // On a phone the seven tiles wrap two to a row instead of crushing; the
  // smaller icon circle leaves room for "In Progress" / "Reschedule".
  const narrowTile = {
    $sm: { minWidth: "40%", flexBasis: "40%" },
    iconCircleSize: sm ? 40 : undefined,
  } as const;

  const renderStatusCards = () => {
    const totalServiceRequests =
      (statusCounts.pending || 0) +
      (statusCounts.open || 0) +
      (statusCounts.in_progress || 0) +
      (statusCounts.closed || 0) +
      (statusCounts.canceled || 0) +
      (statusCounts.reschedule || 0);

    return (
      <YStack gap="$4" $sm={{ gap: "$3" }}>
        {ageDays ? (
          <Text fontSize="$3" color="$white10">
            {t("serviceRequests.olderThan", { count: ageDays })}
          </Text>
        ) : null}
        <XStack gap="$5" flexWrap="wrap" $sm={{ gap: "$3" }}>
          <KPICard
            {...narrowTile}
            neutral
            title={totalServiceRequests}
            description={t("serviceRequests.statuses.all")}
            onPress={() => updateFilters({ status: "All" })}
            isSelected={filters.status === "All"}
          />
          <KPICard
            {...narrowTile}
            warning
            icon={Timer}
            title={statusCounts.pending}
            description={t("serviceRequests.statuses.pending")}
            onPress={() =>
              updateFilters({ status: ServiceRequestStatus.PENDING })
            }
            isSelected={filters.status === ServiceRequestStatus.PENDING}
          />
          <KPICard
            {...narrowTile}
            success
            icon={CheckCircle2}
            title={statusCounts.open}
            description={t("serviceRequests.statuses.open")}
            onPress={() => updateFilters({ status: ServiceRequestStatus.OPEN })}
            isSelected={filters.status === ServiceRequestStatus.OPEN}
          />
          <KPICard
            {...narrowTile}
            info
            icon={Timer}
            title={statusCounts.in_progress || 0}
            description={t("serviceRequests.statuses.inProgress")}
            onPress={() =>
              updateFilters({ status: ServiceRequestStatus.IN_PROGRESS })
            }
            isSelected={filters.status === ServiceRequestStatus.IN_PROGRESS}
          />
        </XStack>
        <XStack gap="$5" flexWrap="wrap" $sm={{ gap: "$3" }}>
          <KPICard
            {...narrowTile}
            error
            icon={X}
            title={statusCounts.canceled}
            description={t("serviceRequests.statuses.canceled")}
            onPress={() =>
              updateFilters({ status: ServiceRequestStatus.CANCELED })
            }
            isSelected={filters.status === ServiceRequestStatus.CANCELED}
          />
          <KPICard
            {...narrowTile}
            info
            icon={Calendar}
            title={statusCounts.reschedule}
            description={t("serviceRequests.statuses.reschedule")}
            onPress={() =>
              updateFilters({ status: ServiceRequestStatus.RESCHEDULE })
            }
            isSelected={filters.status === ServiceRequestStatus.RESCHEDULE}
          />
          <KPICard
            {...narrowTile}
            neutral
            icon={CheckCircle2}
            title={statusCounts.closed}
            description={t("serviceRequests.statuses.closed")}
            onPress={() =>
              updateFilters({ status: ServiceRequestStatus.CLOSED })
            }
            isSelected={filters.status === ServiceRequestStatus.CLOSED}
          />
        </XStack>
      </YStack>
    );
  };

  const renderPagination = () => (
    <XStack
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap="$3"
      marginTop="$6"
      paddingHorizontal="$4"
      $xs={{ paddingHorizontal: 0 }}
    >
      <Text fontSize="$3" color="$gray10">
        Page {pagination.currentPage}{" "}
        {pagination.hasMore ? "(more available)" : "(last page)"}
      </Text>
      <XStack gap="$2">
        <OutlinedButton
          size="$3"
          onPress={goToPreviousPage}
          disabled={pagination.currentPage === 1}
        >
          Previous
        </OutlinedButton>
        <GradientButton
          size="$3"
          onPress={goToNextPage}
          disabled={!pagination.hasMore}
        >
          Next
        </GradientButton>
      </XStack>
    </XStack>
  );

  const renderServiceRequestCards = () => (
    <YStack gap="$3">
      {serviceRequestsWithCustomer.length > 0 ? (
        serviceRequestsWithCustomer.map((request) => (
          <ServiceRequestTableRow
            key={request.id}
            request={request}
            onViewServiceRequest={handleViewServiceRequest}
            asCard
          />
        ))
      ) : (
        <Text padding="$4" textAlign="center">
          {t("common.noResults", "No service requests found for this tab.")}
        </Text>
      )}
    </YStack>
  );

  const renderServiceRequestTable = () => {
    // Eight columns crush below desktop width; keep them readable and let the
    // table scroll sideways instead.
    const table = (
      <Table width={lg ? 1000 : undefined}>
        <Table.Head>
          <Table.Row rowLocation="first">
            <Table.HeaderCell>
              {t("serviceRequests.tableHeaders.customerName", "Customer Name")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("serviceRequests.tableHeaders.serviceType", "Service Type")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("serviceRequests.tableHeaders.status", "Status")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("serviceRequests.tableHeaders.systemAlert", "System Alert")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t(
                "serviceRequests.tableHeaders.customerRequest",
                "Customer Request",
              )}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t(
                "serviceRequests.tableHeaders.requestClosed",
                "Request Closed",
              )}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("serviceRequests.tableHeaders.deviceRoom", "Device / Room")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t("serviceRequests.tableHeaders.action", "Action")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {serviceRequestsWithCustomer.length > 0 ? (
            serviceRequestsWithCustomer.map((request, index) => (
              <ServiceRequestTableRow
                key={request.id}
                request={request}
                onViewServiceRequest={handleViewServiceRequest}
                isFirst={index === 0}
                isLast={index === serviceRequestsWithCustomer.length - 1}
              />
            ))
          ) : (
            <Table.Row>
              <Table.Cell>
                <Text padding="$4" textAlign="center" width="100%">
                  {t(
                    "common.noResults",
                    "No service requests found for this tab.",
                  )}
                </Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    );

    return (
      <YStack space="$4">
        {lg ? <ScrollView horizontal>{table}</ScrollView> : table}
      </YStack>
    );
  };
  // #endregion

  return (
    <YStack
      flex={1}
      padding="$4"
      gap="$4"
      backgroundColor="$backgroundSoft"
      $sm={{ paddingHorizontal: 0 }}
    >
      {renderHeader()}
      {renderFilters()}
      {renderStatusCards()}

      {isLoading && renderLoading()}

      {error && renderError()}

      {!isLoading && !error && (
        <YStack gap="$4">
          {sm ? renderServiceRequestCards() : renderServiceRequestTable()}
          {renderPagination()}
        </YStack>
      )}
    </YStack>
  );
};

export default ServiceRequestsList;
